import React, { useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';

import LeaderboardTableContainer from '../containers/leaderboard_table_container';
import ChevronIcon from './chevron_icon';
import useScrollState from '../hooks/useScrollState';
import useScrollRail from '../hooks/useScrollRail'
import usePortWidth from '../hooks/usePortWidth'
import useFadeBands from '../hooks/useFadeBands'
import useColumnSync from '../hooks/useColumnSync'
import { VIEW_MODES } from '../constants';
import {isMobileSurface} from '../utils/device_utils'
import { componentText } from '../utils/i18n_utils'

const ResultScope = ({
  eventId,
  resultScopeId,
  favorites,
  viewMode,
  isExpanded,
  showFullLeaderboard,
  toggleFullLeaderboard,
  isManagerSite,
}) => {
  const scrollRef = useRef(null);
  const scopeRef = useRef(null)
  useFadeBands(scopeRef)

  const { isAtStart, isAtEnd, isScrollable, checkScrollable } = useScrollState(scrollRef)
  usePortWidth(scrollRef, checkScrollable)

  // Mobile sizes every column itself, so only the content-driven views need
  // the favorites table pinned to the full table's measured columns.
  useColumnSync(scopeRef, viewMode !== VIEW_MODES.MOBILE)

  // Horizontal scrollbar
  const onDesktop = !isMobileSurface()
  const showRail = onDesktop && viewMode === VIEW_MODES.WEB && isScrollable
  const { railRef, contentWidth } = useScrollRail(scrollRef, showRail)

  // Re-check scrollable when the full leaderboard section is toggled (content height changes)
  useLayoutEffect(() => {
    checkScrollable();
  }, [showFullLeaderboard, checkScrollable]);

  const hasFavorites = React.useMemo(() => {
    if (Array.isArray(favorites)) return favorites.length > 0;
    return Object.keys(favorites || {}).length > 0;
  }, [favorites]);

  const expand = viewMode === VIEW_MODES.MOBILE
    ? (isExpanded ? 'expanded' : '')
    : (isScrollable ? 'expanded' : '');

  // Each edge follows whether there is still table that way, mirroring the
  // scorecards. Mobile only scrolls once expanded, so it gates on that.
  const canScroll = isScrollable && (viewMode === VIEW_MODES.MOBILE ? isExpanded : true)

  const isScrolled = canScroll && !isAtStart
  const showGradient = canScroll && !isAtEnd

  const showsFull = showFullLeaderboard || isManagerSite
  // A collapsed full leaderboard stays mounted, hidden, on the content-driven
  // views: useColumnSync pins the favorites table to its measured columns, and
  // a table that is not rendered has none. Mobile sizes its columns itself and
  // keeps unmounting it, so it does not pay for the hidden rows.
  const keepsFullMeasurable = !showsFull && hasFavorites && viewMode !== VIEW_MODES.MOBILE

  return (
    <div ref={scopeRef} className={`lbv2__result-scope ${showGradient ? 'with-gradient' : 'scrolled'}`} id={resultScopeId}>
      {showRail && (
        <div className="lbv2__scroll-rail" ref={railRef} aria-hidden="true">
          <div className="lbv2__scroll-rail-spacer" style={{ width: contentWidth }} />
        </div>
      )}
      <div
        ref={scrollRef}
        className={`lbv2__scroll-wrapper ${expand} ${isScrolled ? 'scrolled' : ''}`}
      >
        {hasFavorites && (
          <LeaderboardTableContainer
            eventId={eventId}
            resultScopeId={resultScopeId}
            isFavorite={true}
            viewMode={viewMode}
          />
        )}

        {hasFavorites && (
          <div className="lbv2__full-leaderboard sticky">
            <button
              className={`lbv2__full-leaderboard-btn ${showFullLeaderboard ? 'active' : ''}`}
              onClick={() => toggleFullLeaderboard(eventId)}
            >
              {viewMode === VIEW_MODES.WEB && <ChevronIcon isOpen={showFullLeaderboard} />}
              <span>{componentText('result_scope.full_leaderboard', 'Full Leaderboard')}</span>
              {[VIEW_MODES.LANDSCAPE, VIEW_MODES.MOBILE].includes(viewMode) && <ChevronIcon isOpen={showFullLeaderboard} />}
            </button>
          </div>
        )}

        {(showsFull || keepsFullMeasurable) && (
          <div className={`lbv2__full-leaderboard-body ${showsFull ? '' : 'is-collapsed'}`}>
            <LeaderboardTableContainer
              eventId={eventId}
              resultScopeId={resultScopeId}
              isFavorite={false}
              viewMode={viewMode}
            />
          </div>
        )}
      </div>
    </div>
  );
};

ResultScope.propTypes = {
  eventId: PropTypes.string.isRequired,
  resultScopeId: PropTypes.string.isRequired,
  favorites: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isExpanded: PropTypes.bool,
  showFullLeaderboard: PropTypes.bool,
  toggleFullLeaderboard: PropTypes.func,
  isManagerSite: PropTypes.bool,
};

export default ResultScope;
